import { memo, useRef, useState } from "react";
import { Box, Checkbox, Paper, Typography } from "@mui/material";
import { CloudUpload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CustomButton } from "../../components/common/customButton";
import { renderSubTitle } from "../../components/renderComponents";
import { useDealProvider } from "../../context/DealContext";
import { createExtraction } from "../../services/extractionApi";

const ACCEPTED_TYPES =
  ".pdf,.doc,.docx,.xlsx,.xls,.png,.msg,.eml";

type ExtractionMode = "default" | "term_sheet" | "initial_materials";

const MODE_BUTTONS: { value: ExtractionMode; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "term_sheet", label: "Term sheet" },
  { value: "initial_materials", label: "Initial materials" },
];

function isPdfFile(file: File) {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
}

/**
 * AI extraction UX for create and edit deal.
 * Create: files + marketing images.
 * Edit: also Default / Term sheet / Initial materials, plus what to extract.
 */
const DealAiExtraction = ({ dealId = null }: { dealId?: number | null }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { assets } = useDealProvider();
  const isEdit = dealId != null;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [extractionMode, setExtractionMode] =
    useState<ExtractionMode>("default");
  const [extractDeal, setExtractDeal] = useState(true);
  const [extractCommentary, setExtractCommentary] = useState(true);
  const [extractAssetsTenants, setExtractAssetsTenants] = useState(true);
  const [extractImages, setExtractImages] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isTermSheet = isEdit && extractionMode === "term_sheet";
  const isInitialMaterials = isEdit && extractionMode === "initial_materials";
  const isRestrictedMode = isTermSheet || isInitialMaterials;
  const acceptTypes = isInitialMaterials ? ".pdf" : ACCEPTED_TYPES;

  const addFiles = (files: FileList | File[]) => {
    let next = Array.from(files);
    if (isInitialMaterials) {
      next = next.filter(isPdfFile);
      if (next.length === 0) {
        setErrorMessage("Initial materials only accept PDF files.");
        return;
      }
    }
    if (next.length === 0) return;
    setSelectedFiles((prev) => [...prev, ...next]);
    setErrorMessage(null);
  };

  const handleModeChange = (mode: ExtractionMode) => {
    setExtractionMode(mode);
    if (mode === "default") return;
    setExtractDeal(true);
    setExtractCommentary(false);
    setExtractAssetsTenants(false);
    if (mode === "initial_materials") {
      setSelectedFiles((prev) => prev.filter(isPdfFile));
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      addFiles(event.target.files);
    }
    event.target.value = "";
  };

  const handleDrag = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setDragActive(true);
    } else if (event.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      addFiles(event.dataTransfer.files);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const goToExtractionsList = (
    extractionIds: number[],
    highlightedDuplicate: boolean,
    duplicateMessage?: string,
  ) => {
    if (extractionIds.length === 0) return;

    const params = new URLSearchParams({
      highlight: String(extractionIds[0]),
    });
    if (highlightedDuplicate) {
      params.set("duplicate", "true");
    }

    navigate(`/ai-extractions?${params.toString()}`, {
      state:
        highlightedDuplicate && duplicateMessage
          ? { duplicateMessage }
          : undefined,
    });
  };

  const handleExtract = async () => {
    if (selectedFiles.length === 0) return;

    setIsExtracting(true);
    setErrorMessage(null);

    try {
      const assetNames = (assets || [])
        .map((asset) =>
          typeof asset.asset_name === "string" ? asset.asset_name.trim() : "",
        )
        .filter(Boolean);

      const extractionIds: number[] = [];
      let highlightedDuplicate = false;
      let duplicateMessage: string | undefined;

      // Create one async extraction job per file
      for (const file of selectedFiles) {
        const response = await createExtraction(file, {
          excel_provider: "anthropic_column_matching",
          is_term_sheet: isTermSheet,
          is_initial_materials: isInitialMaterials,
          extract_images: extractImages,
          assets: assetNames.length > 0 ? assetNames : undefined,
          ...(isEdit
            ? {
                deal_id: dealId,
                extract_deal: isRestrictedMode ? true : extractDeal,
                extract_commentary: isRestrictedMode
                  ? false
                  : extractCommentary,
                extract_assets_tenants: isRestrictedMode
                  ? false
                  : extractAssetsTenants,
              }
            : {}),
        });

        if (response?.success && response.data) {
          const { id, duplicate, message } = response.data;
          extractionIds.push(id);
          if (extractionIds.length === 1 && duplicate) {
            highlightedDuplicate = true;
            duplicateMessage = message;
          }
        } else {
          const message =
            typeof response?.message === "string"
              ? response.message
              : `Failed to start extraction for ${file.name}.`;
          setErrorMessage(message);
          // Keep going with other files if some already succeeded
        }
      }

      goToExtractionsList(
        extractionIds,
        highlightedDuplicate,
        duplicateMessage,
      );
    } catch (error) {
      console.error("Error extracting data:", error);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while extracting data.",
      );
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: "12px",
        border: dragActive ? "2px dashed #1976d2" : "1px solid #e5e7eb",
        backgroundColor: dragActive ? "#f0f7ff" : "white",
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)",
        transition: "all 0.2s ease",
      }}
    >
      {renderSubTitle("Extract Information with AI")}

      <Box
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          marginTop: 1,
          padding: 3,
          textAlign: "center",
          cursor: "pointer",
          mb: 2,
          backgroundColor: dragActive ? "#e3f2fd" : "#fafafa",
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#1976d2",
          },
        }}
        onClick={handleBrowseClick}
      >
        <CloudUpload
          sx={{
            fontSize: 48,
            color: dragActive ? "#1976d2" : "#999",
            mb: 1,
          }}
        />
        <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0.5 }}>
          {dragActive ? "Drop files here" : "Drag & drop files here"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1 }}
        >
          {isInitialMaterials
            ? "Supported format: PDF only"
            : "Supported formats: PDF, XLSX, XLS, PNG, MSG, EML (Outlook emails)"}
        </Typography>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept={acceptTypes}
        onChange={handleFileChange}
      />

      {isEdit && (
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", mb: 1, color: "#55575C" }}
          >
            Extraction mode
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {MODE_BUTTONS.map((mode) => {
              const selected = extractionMode === mode.value;
              return (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => handleModeChange(mode.value)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "6px",
                    border: selected
                      ? "1px solid #1B7A63"
                      : "1px solid #d0d5dd",
                    backgroundColor: selected ? "#1B7A63" : "white",
                    color: selected ? "white" : "#55575C",
                    fontSize: "13px",
                    fontWeight: selected ? 600 : 500,
                    cursor: "pointer",
                  }}
                >
                  {mode.label}
                </button>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            fontSize: "14px",
            color: "#55575C",
            cursor: "pointer",
          }}
        >
          <Checkbox
            disableRipple
            checked={extractImages}
            onChange={(e) => setExtractImages(e.target.checked)}
            sx={{ color: "#1B7A63 !important", p: 0, mr: 1, mt: "2px" }}
          />
          <Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "#55575C" }}
            >
              Extract marketing images
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Extracts photos from PDF, MSG, or EML files
            </Typography>
          </Box>
        </label>
      </Box>

      {isEdit && extractionMode === "default" && (
        <Box sx={{ mb: 2, p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: "bold", mb: 1, color: "#55575C" }}
          >
            Extract Options:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                color: "#55575C",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={extractDeal}
                onChange={(e) => setExtractDeal(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Deal Data
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                color: "#55575C",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={extractCommentary}
                onChange={(e) => setExtractCommentary(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Commentary
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "14px",
                color: "#55575C",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={extractAssetsTenants}
                onChange={(e) => setExtractAssetsTenants(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              Assets/Tenants
            </label>
          </Box>
        </Box>
      )}

      {isTermSheet && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 2,
            color: "#999",
            fontStyle: "italic",
          }}
        >
          Term sheets only extract deal data (PDF or other supported files).
        </Typography>
      )}

      {isInitialMaterials && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 2,
            color: "#999",
            fontStyle: "italic",
          }}
        >
          Initial materials populate loan sizing, fees, covenants, and related
          deal fields only. Commentary, assets, and comps are not extracted.
        </Typography>
      )}

      {selectedFiles.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            {selectedFiles.map((file, index) => (
              <Box
                key={`${file.name}-${index}`}
                sx={{
                  mb: 1,
                  p: 1.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#55575C",
                    fontWeight: "bold",
                  }}
                >
                  {file.name}
                </Typography>
                <CustomButton
                  text="Remove"
                  handleButtonPress={() => handleRemoveFile(index)}
                  isButtonDisabled={isExtracting}
                  solid={false}
                  widthOverride="90px"
                  heightOverride="28px"
                />
              </Box>
            ))}
          </Box>

          <CustomButton
            text={isExtracting ? "Extracting..." : "Extract"}
            handleButtonPress={handleExtract}
            isButtonDisabled={isExtracting}
            solid={true}
            widthOverride="150px"
          />
        </>
      )}

      {errorMessage && (
        <Typography sx={{ mt: 2, fontSize: "14px", color: "#d32f2f" }}>
          {errorMessage}
        </Typography>
      )}
    </Paper>
  );
};

// Memoized so parent forms (e.g. CreateDealUpdate) can re-render on each
// keystroke without re-rendering this component.
export default memo(DealAiExtraction);
